import { MouseEvent } from 'react';
import styled from 'styled-components';
import { CHIP_CATEGORY_COLORS, ChipCategory } from '@models/chips';

interface Props {
  label: string;
  category: ChipCategory;
  isSelected?: boolean;
  isCustom?: boolean;
  /** Render in a distinct (pink) color to mark this chip as shared with the viewer. */
  isShared?: boolean;
  onClick?: (e: MouseEvent) => void;
}

// Shared-trait highlight palette. Distinct from the standard purple so users
// can tell at a glance which chips overlap with their own profile.
const SHARED_COLORS = { bg: '#FFE6F4', text: '#FF00A8', border: '#FF00A8' };

function CategoryChip({
  label,
  category,
  isSelected = false,
  isCustom = false,
  isShared = false,
  onClick,
}: Props) {
  const baseColors = CHIP_CATEGORY_COLORS[category] ?? {
    bg: '#F3E8FF',
    text: '#8700FF',
    border: '#8700FF',
  };
  const colors = isShared ? SHARED_COLORS : baseColors;

  return (
    <ChipContainer
      $bgColor={isSelected ? colors.bg : '#FFFFFF'}
      $borderColor={isSelected ? colors.border : '#E0E0E0'}
      $textColor={isSelected ? colors.text : '#757575'}
      $isSelected={isSelected}
      onClick={onClick}
      role="button"
      tabIndex={0}
    >
      {isCustom && <CustomIndicator $color={colors.text}>+</CustomIndicator>}
      <ChipLabel>{label}</ChipLabel>
    </ChipContainer>
  );
}

const ChipContainer = styled.div<{
  $bgColor: string;
  $borderColor: string;
  $textColor: string;
  $isSelected: boolean;
}>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 8px;
  border: 1px solid ${({ $borderColor }) => $borderColor};
  background-color: ${({ $bgColor }) => $bgColor};
  color: ${({ $textColor }) => $textColor};
  cursor: pointer;
  user-select: none;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
  -webkit-tap-highlight-color: transparent;
  white-space: nowrap;
  font-size: 14px;
  font-weight: ${({ $isSelected }) => ($isSelected ? 600 : 400)};
  line-height: 20px;

  &:active {
    opacity: 0.8;
  }
`;

const ChipLabel = styled.span`
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CustomIndicator = styled.span<{ $color: string }>`
  font-weight: 700;
  font-size: 14px;
  color: ${({ $color }) => $color};
`;

export default CategoryChip;
